import { Module } from 'vuex';
import {
  University,
  CreditScore,
  CosignerCreditScore,
  Degree,
  RateType,
  RepaymentPlan,
  Lender,
  Bin,
} from '@/store/models';

const emptyBin = (index) => ({
  frequency:0,
  minimumValue:index + 1,
  maximumValue:index + 2,
});
const emptyBins = [...new Array(7)]
  .map((_, index) => emptyBin(index));
const localStorageExists = localStorage != null;
function getValuesFromLocalStorage(key, defaultValue){
  if (localStorageExists) {
    if(localStorage.getItem(key)) {
      return JSON.parse(<string>localStorage.getItem(key));
    }

    return defaultValue || null;
  }
}
interface BaseState {
  hideFooter: Boolean,
  loading: Boolean,
  showCosignerCreditScores: boolean,
  universities?: Array<University>;
  creditScores?: Array<CreditScore>;
  cosignerCreditScores?: Array<CosignerCreditScore>;
  degrees?: Array<Degree>;
  rateTypes?: Array<RateType>;
  repaymentPlans?: Array<RepaymentPlan>;
  lenders?: Array<Lender>;
  bins?: Array<Bin>;
  selectedLoanTypes: Array<string>;
  selectedCreditScores: Array<CreditScore>;
  selectedCosignerCreditScores: Array<CosignerCreditScore>;
  selectedUniversities: Array<University>;
  selectedDegrees: Array<Degree>;
  selectedRateTypes: Array<RateType>;
  selectedRepaymentPlans: Array<RepaymentPlan>;
  selectedLenders: Array<Lender>;
}
const base: Module<BaseState, {}> = {
  namespaced: true,
  state: {
    hideFooter: false,
    loading: true,
    showCosignerCreditScores: getValuesFromLocalStorage('showCosignerCreditScores', false),
    creditScores: [],
    cosignerCreditScores: [],
    universities: [],
    degrees: [],
    lenders: [],
    rateTypes: [],
    repaymentPlans: [],
    selectedLoanTypes: getValuesFromLocalStorage('selectedLoanTypes', []),
    selectedCreditScores: getValuesFromLocalStorage('selectedCreditScores', []),
    selectedCosignerCreditScores: getValuesFromLocalStorage('selectedCosignerCreditScores', []),
    selectedUniversities: getValuesFromLocalStorage('selectedUniversities', []),
    selectedDegrees: getValuesFromLocalStorage('selectedDegrees', []),
    selectedRateTypes: getValuesFromLocalStorage('selectedRateTypes', []),
    selectedRepaymentPlans: getValuesFromLocalStorage('selectedRepaymentPlans', []),
    selectedLenders: getValuesFromLocalStorage('selectedLenders', []),
    bins: [...emptyBins],
  },
  getters: {
    reversedCreditScores: state => {
      return (state.creditScores || []).slice().reverse();
    },
    reversedCosignerCreditScores: state => {
      return (state.cosignerCreditScores || [])
        .slice()
        .filter(score => score.id !== 1)
        .reverse();
    },
  },
  mutations: {
    setUniversities(state, universities: Array<University>) {
      state.universities = universities;
    },

    setCreditScores(state, creditScores: Array<CreditScore>) {
      state.creditScores = creditScores;
    },

    setCosignerCreditScores(state, cosignerCreditScores: Array<CreditScore>) {
      state.cosignerCreditScores = cosignerCreditScores;
    },

    setDegrees(state, degrees: Array<Degree>) {
      state.degrees = degrees;
    },

    setRateTypes(state, rateTypes: Array<RateType>) {
      state.rateTypes = rateTypes;
    },

    setRepaymentPlans(state, repaymentPlans: Array<RepaymentPlan>) {
      state.repaymentPlans = repaymentPlans;
    },

    setLenders(state, lenders: Array<Lender>) {
      state.lenders = lenders;
    },

    setBins(state, bins: Array<Bin>) {
      state.bins = bins;
    },

    setLoading(state, loading  = true) {
      state.loading = loading;
    },

    toggleCosigners(state, value) {
      state.showCosignerCreditScores = value;
      if (localStorageExists) {
        localStorage.setItem('showCosignerCreditScores', JSON.stringify(value));
      }
    },

    setSelectedValue(state, {key, val}) {
      const computedKey = `selected${key}`;
      state[computedKey] = val;
      if (localStorageExists) {
        localStorage.setItem(computedKey, JSON.stringify(val));
      }
    },

    setFooterDisplay(state, show) {
      state.hideFooter = !show;
    },
  },

  actions: {
    setAllOptions(store, { data }) {
      store.commit('setUniversities', data.universities);
      store.commit('setCreditScores', data.creditScores);
      store.commit('setDegrees', data.degrees);
      store.commit('setRateTypes', data.rateTypes);
      store.commit('setRepaymentPlans', data.repaymentPlans);
      store.commit('setLenders', data.lenders);
      store.commit('setCosignerCreditScores', data.cosignerCreditScores);
    },

    setSelectedValue(store, {key, val}) {
      store.commit('setSelectedValue', {key, val});
    },

    setBins(store, { bins }) {
      store.commit('setBins', bins);
      setTimeout(() => {
        store.commit('setLoading', false);
      }, 500);
    },

    toggleCosigners(store, value) {
      store.commit('toggleCosigners', value);
      if (value === false) {
        store.commit('setSelectedValue', {
          key: 'CosignerCreditScores',
          val: [],
        });
      }
    },

    setLoading(store, loading  = true) {
      store.commit('setLoading', loading);
    },

    setFooterDisplay(store, show = false) {
      store.commit('setFooterDisplay', show);
    },
  },
};

export default base;
